import { useEffect, useMemo, useState } from 'react';
import { SettingClient } from '../../../helpers/client';
import { useLocation } from 'react-router';
import { Box, IconButton } from '@mui/material';
import { useAppDispatch } from '../../../redux/hooks';
import { appStyleMode, basePath } from '../../../helpers/clientConfigs';
import * as externalLogin from '../../../helpers/externalLogin';
import { Spinner } from '../../spinner/spinner';
import { AppStyle } from '../../../helpers/appStyle';
import { authenticateFromGoogle } from '../../../redux/reducers/authReducer';
import { GoogleLoginError, GoogleSignInProps } from '../../../interfaces/interfaces';
import { setRedirectAfterLogin } from '../../../helpers/localStorageService';
import { ExternalAuthenticationRequest } from 'orderme-api-integration-client';
import { useRouteNavigator } from '../../../routes/useRouteNavigator';
import GoogleIcon from './googleIcon';

export function GoogleSignIn(props: GoogleSignInProps) {
  const dispatch = useAppDispatch();
  const routeNavigator = useRouteNavigator();
  const location = useLocation();
  const [clientId, setClientId] = useState('');
  const settingClient = useMemo(() => new SettingClient(), []);
  const [isBusy, setIsBusy] = useState(false);

  const handleGoogleSignIn = async (accessToken: string, state: string, code: string) => {
    const googleState = externalLogin.getGoogleState();
    externalLogin.removeGoogleState();

    var googleToken = externalLogin.getGoogleTokenProps(accessToken);
    if (!accessToken || !code || state !== googleState.state || googleToken?.nonce !== googleState.nonce) {
      if (props.onError) {
        props.onError('Google OAuth signin failed');
      }

      return;
    }

    try {
      const authRequest = { accessToken: accessToken, state: state, code: code } as ExternalAuthenticationRequest;
      await dispatch(authenticateFromGoogle(authRequest)).unwrap();
      if (props.onSignedIn) {
        props.onSignedIn();
      }
    } catch (err) {
      console.log(err);

      const loginError = err as GoogleLoginError;
      if (loginError?.userGrantRequired) {
        signIn(true, googleToken.email);
        return;
      }

      const registerState = externalLogin.getGoogleRegistrationProps(accessToken, state, code);
      routeNavigator.goToRegistration({ state: registerState, replace: true });
    }
  };

  useEffect(() => {
    if (clientId !== '') {
      return;
    }

    (async () => {
      let setting = await settingClient.get();

      if (!setting?.authentication?.googleClientId) {
        return;
      }

      setClientId(setting.authentication.googleClientId);
    })();
  }, [clientId, settingClient]);

  useEffect(() => {
    if (props.userGrantRequired) {
      signIn(true, props.loginHint);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.userGrantRequired]);

  const signIn = (userGrantRequired: boolean, loginHint?: string) => {
    setIsBusy(true);

    const state = externalLogin.randomState();
    const nonce = externalLogin.randomNonce();
    externalLogin.saveGoogleState(state, nonce);

    let redirectUrl = basePath + '/signin-google';
    let endpoint =
      `https://accounts.google.com/o/oauth2/v2/auth` +
      `?response_type=code id_token` +
      `&nonce=${nonce}` +
      `&state=${state}` +
      `&access_type=offline` +
      `&redirect_uri=${redirectUrl}` +
      `&client_id=${clientId}` +
      `&scope=openid profile email`;

    if (userGrantRequired) {
      endpoint += `&prompt=consent`;
    }

    if (loginHint) {
      endpoint += `&login_hint=${loginHint}`;
    }

    if (appStyleMode === AppStyle.Mobile) {
      signInMobileApp(endpoint);
    } else {
      setRedirectAfterLogin(location.pathname + location.search);
      window.location.replace(endpoint);
    }
  };

  const signInMobileApp = (endpoint: string) => {
    const browser = window.cordova.InAppBrowser.open(endpoint, '_blank', 'location=no');
    let oauthCompleted = false;

    const onLoadStart = async (event: any) => {
      const url: string = event.url;
      const callbackUrl = 'lt.paulini.mobile://oauth_callback';
      if (url.startsWith(callbackUrl) || url.startsWith('http://' + callbackUrl)) {
        oauthCompleted = true;
        browser.removeEventListener('loadstart', onLoadStart);
        browser.close();
        const fragment = url.includes('?') ? url.split('?')[1] : '';
        const params = new URLSearchParams(fragment);
        try {
          await handleGoogleSignIn(
            params.get('access_token') ?? '',
            params.get('state') ?? '',
            params.get('code') ?? ''
          );
        } finally {
          setIsBusy(false);
        }
      }
    };
    browser.addEventListener('loadstart', onLoadStart);
    browser.addEventListener('exit', () => {
      if (!oauthCompleted) {
        setIsBusy(false);
      }
    });
  };

  return (
    props.isVisible && (
      <Box>
        <IconButton
          onClick={() => signIn(false)}
          sx={{
            border: '1px solid black',
            borderRadius: '10px',
            px: '20px',
            py: '13px',
          }}
        >
          <GoogleIcon sx={{ height: '22px' }} />
        </IconButton>
        <Spinner isActive={isBusy} />
      </Box>
    )
  );
}
